import Checkbox from '@repo/ui/checkbox'
import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getCountries } from '../../../api/countries/get-countries'
import {
  EndOfJourneyReviewsSummaryFilter,
  SummaryScanStatus
} from '../../../api/end-of-journey-reviews/summary/types'
import { getFormulas } from '../../../api/formula/get-formulas'
import { getOls } from '../../../api/ols/get-ols'
import { getOptions } from '../../../api/options/get-options'
import { getPeriods } from '../../../api/periods/get-periods'
import CountryFlag from '../../../components/elements/country-flag'
import useTranslate from '../../../lib/hooks/use-translate'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { LocaleKeys } from '../../../locale/translation-types'
import { getOlName } from '../../ols/center/utils/functions'
import { yearsFilterOptions } from '../../pcr-personal-space/utils'

type Props = {
  filter?: EndOfJourneyReviewsSummaryFilter
  onFilterChange: (filter: EndOfJourneyReviewsSummaryFilter) => void
}

const scanStatusOptions: { label: LocaleKeys; value: SummaryScanStatus | undefined }[] = [
  { label: 'end.of.stay.appreciations.summary.filter.scan.status.all', value: undefined },
  {
    label: 'end.of.stay.appreciations.summary.filter.scan.status.not.scanned',
    value: 'NOT_SCANNED'
  },
  { label: 'end.of.stay.appreciations.summary.filter.scan.status.incomplete', value: 'INCOMPLETE' }
]

const StarsFilter = ({
  title,
  max,
  ticked = [],
  disabled,
  onChange
}: {
  title: string
  max: number
  ticked?: number[]
  disabled?: boolean
  onChange: (stars: number[]) => void
}) => {
  const { t } = useTranslate()
  return (
    <div className="flex flex-col gap-2">
      <Typography variant="label-semibold">{title}</Typography>
      {Array.from({ length: max }, (_, index) => max - index).map((stars) => (
        <Checkbox
          key={stars}
          label={t(
            stars > 1
              ? 'end.of.stay.appreciations.summary.filter.stars'
              : 'end.of.stay.appreciations.summary.filter.star',
            { stars }
          )}
          checked={ticked.includes(stars)}
          disabled={disabled}
          onChange={(checked) =>
            onChange(checked ? [...ticked, stars] : ticked.filter((value) => value !== stars))
          }
        />
      ))}
    </div>
  )
}

/** "Filtrer par" panel of the end-of-stay ratings summary (card 139). */
const EndOfStayAppreciationsSummaryFilter = ({ filter, onFilterChange }: Props) => {
  const { t } = useTranslate()
  const setFilter = <K extends keyof EndOfJourneyReviewsSummaryFilter>(
    key: K,
    value: EndOfJourneyReviewsSummaryFilter[K]
  ) => onFilterChange({ ...filter, [key]: value })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const { data: options } = useQuery({
    queryKey: ['options', { version: 'LIGHT' }],
    queryFn: () => getOptions({ version: 'LIGHT' })
  })

  const { data: ols } = useQuery({
    queryKey: ['ols'],
    queryFn: () => getOls()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  if (!filter) return null

  // "Centres non scannés" have no ratings: the backend ignores the star filters for them
  const starsDisabled = filter.scanStatus === 'NOT_SCANNED'

  return (
    <div className="flex flex-col gap-3">
      <MultiSelectInput
        label={t('end.of.stay.appreciations.summary.filter.country')}
        variant="primary"
        searchable
        options={countries?.data.map(({ name, id }) => ({
          label: name,
          value: id,
          custom: <CountryFlag possibleCountry={name} />
        }))}
        selectedOptions={filter.countryIds?.map((id) => ({
          label: countries?.data.find((country) => country.id === id)?.name ?? '',
          value: id
        }))}
        onChange={(countryIds) => setFilter('countryIds', countryIds)}
      />

      <MultiSelectInput
        label={t('end.of.stay.appreciations.summary.filter.formula')}
        variant="primary"
        searchable
        options={formulas?.data.map((formula) => ({
          label: getTranslatedName(formula) || '—',
          value: formula.id
        }))}
        selectedOptions={filter.formulaIds?.map((id) => ({
          label: getTranslatedName(formulas?.data.find((formula) => formula.id === id)) || '—',
          value: id
        }))}
        onChange={(formulaIds) => setFilter('formulaIds', formulaIds)}
      />

      <MultiSelectInput
        label={t('end.of.stay.appreciations.summary.filter.theme')}
        variant="primary"
        searchable
        options={options?.data.map((option) => ({
          label: getTranslatedName(option),
          value: option.id
        }))}
        selectedOptions={filter.themeIds?.map((id) => ({
          label: getTranslatedName(options?.data.find((option) => option.id === id)),
          value: id
        }))}
        onChange={(themeIds) => setFilter('themeIds', themeIds)}
      />

      <MultiSelectInput
        label={t('end.of.stay.appreciations.summary.filter.ol')}
        variant="primary"
        searchable
        options={ols?.data.map((ol) => ({ label: getOlName(ol), value: ol.id }))}
        selectedOptions={filter.olIds?.map((id) => ({
          label: getOlName(ols?.data.find((ol) => ol.id === id)),
          value: id
        }))}
        onChange={(olIds) => setFilter('olIds', olIds)}
      />

      <MultiSelectInput
        label={t('end.of.stay.appreciations.summary.filter.periods')}
        variant="warning"
        searchable
        options={periods?.data.map((period) => ({ label: period.name, value: period.id }))}
        selectedOptions={filter.periodIds?.map((id) => ({
          label: periods?.data.find((period) => period.id === id)?.name ?? '',
          value: id
        }))}
        onChange={(periodIds) => setFilter('periodIds', periodIds)}
      />

      <SelectInput
        label={t('end.of.stay.appreciations.summary.filter.year')}
        options={yearsFilterOptions}
        selectedOption={yearsFilterOptions.find(({ value }) => value === filter.year)}
        onChange={(year) => setFilter('year', year)}
      />

      <Checkbox
        label={t('end.of.stay.appreciations.summary.filter.include.oto')}
        checked={filter.includeOto === true}
        onChange={(checked) => setFilter('includeOto', checked)}
      />

      <StarsFilter
        title={t('end.of.stay.appreciations.summary.filter.five.stars')}
        max={5}
        ticked={filter.fiveStars}
        disabled={starsDisabled}
        onChange={(fiveStars) => setFilter('fiveStars', fiveStars)}
      />

      <StarsFilter
        title={t('end.of.stay.appreciations.summary.filter.three.stars')}
        max={3}
        ticked={filter.threeStars}
        disabled={starsDisabled}
        onChange={(threeStars) => setFilter('threeStars', threeStars)}
      />

      <div className="flex flex-col gap-2">
        {scanStatusOptions.map(({ label, value }) => (
          <RadioInput
            key={label}
            label={t(label)}
            checked={filter.scanStatus === value}
            onCheck={() => setFilter('scanStatus', value)}
          />
        ))}
      </div>
    </div>
  )
}

export default EndOfStayAppreciationsSummaryFilter
